<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import SectionLayout from './SectionLayout.vue';

// Encerramento automático por inatividade e a regra de pular conversas aguardando o
// agente, que vale também para o follow-up (Conversations::ResolutionJob).
const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const MIN_DURATION = 10;
const MAX_DURATION = 1439856;

const getDurationUnit = duration => {
  if (duration && duration % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (duration && duration % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

const autoResolveEnabled = ref(false);
const autoResolveDuration = ref(30);
const autoResolveDurationUnit = ref(getDurationUnit(30));
const ignoreWaiting = ref(false);
const isSubmitting = ref(false);

watch(
  currentAccount,
  () => {
    const { auto_resolve_after, auto_resolve_ignore_waiting } =
      currentAccount.value?.settings || {};
    const savedDuration = Number(auto_resolve_after);
    autoResolveEnabled.value = savedDuration >= MIN_DURATION;
    autoResolveDuration.value = autoResolveEnabled.value ? savedDuration : 30;
    autoResolveDurationUnit.value = getDurationUnit(autoResolveDuration.value);
    ignoreWaiting.value = !!auto_resolve_ignore_waiting;
  },
  { deep: true, immediate: true }
);

const isDurationInvalid = computed(() => {
  const duration = Number(autoResolveDuration.value);
  return duration < MIN_DURATION || duration > MAX_DURATION;
});

const save = async () => {
  if (autoResolveEnabled.value && isDurationInvalid.value) return;

  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        auto_resolve_after: autoResolveEnabled.value
          ? Number(autoResolveDuration.value)
          : null,
        auto_resolve_ignore_waiting: ignoreWaiting.value,
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

// Desligar o encerramento automático salva na hora; ligar abre o campo de tempo.
const toggleAutoResolve = async () => {
  if (!autoResolveEnabled.value) await save();
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.INACTIVITY_RESOLUTION.TITLE')"
    :description="t('GENERAL_SETTINGS.INACTIVITY_RESOLUTION.NOTE')"
  >
    <div
      class="grid divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div class="grid gap-4 p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.LABEL') }}
            </p>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.HELP') }}
            </p>
          </div>
          <Switch v-model="autoResolveEnabled" @change="toggleAutoResolve" />
        </div>
        <WithLabel
          v-if="autoResolveEnabled"
          name="auto-resolve-duration"
          :label="t('GENERAL_SETTINGS.INACTIVITY_RESOLUTION.DURATION_LABEL')"
          :has-error="isDurationInvalid"
          :error-message="
            isDurationInvalid
              ? t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.ERROR')
              : ''
          "
        >
          <div
            class="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.45fr)] [&>input]:w-full [&>select]:w-full"
          >
            <DurationInput
              v-model="autoResolveDuration"
              v-model:unit="autoResolveDurationUnit"
              :min="String(MIN_DURATION)"
              :max="String(MAX_DURATION)"
            />
          </div>
        </WithLabel>
      </div>
      <div class="flex items-start justify-between gap-4 p-4">
        <div class="min-w-0">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL') }}
          </p>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            {{
              t('GENERAL_SETTINGS.INACTIVITY_RESOLUTION.IGNORE_WAITING_HELP')
            }}
          </p>
        </div>
        <Switch v-model="ignoreWaiting" />
      </div>
    </div>
    <NextButton
      blue
      class="mt-4 w-fit"
      :disabled="autoResolveEnabled && isDurationInvalid"
      :is-loading="isSubmitting"
      :label="t('GENERAL_SETTINGS.INACTIVITY_RESOLUTION.SAVE')"
      @click="save"
    />
  </SectionLayout>
</template>
